import {
    Container,
    Rectangle,
    Sprite
} from 'pixi.js';
import { Game } from '../../game';
import { Button, FocusManager, InteractiveWrapper } from '../../ui';
import { Disposable, DisposableGroup, LoadContext, GameTime } from '../../utils';
import { IntroMenu } from './IntroMenu';
import { Menus } from './Menus';
import KeyboardJS from 'keyboardjs';

const TIMEOUT = 30000;

export class AccessibilityPopover extends Container
{
    public game: Game;
    private background:Sprite;
    private preloadComplete: Promise<any>;

    public buttons: Button[];

    private introMenu: IntroMenu;
    private groupMenus: Menus;
    private resetButton: Button;

    private viewingIntro: boolean;

    private gestureCatcher: Container;
    private disposables: DisposableGroup;

    private inactiveTimer: number;


    /**
     * This popover emits "close" event when the user is finished.
     */
    constructor(game:Game)
    {
        super();
        this.game = game;
        this.inactiveTimer = 0;
    }

    preload()
    {
        if (this.preloadComplete)
        {
            return this.preloadComplete;
        }

        const loader = LoadContext.current;
        loader.add('accessibilityBG', './assets/accessibility/bg_setup{size}.jpg');
        loader.add('SharedUI', './assets/shared/SharedUI{size}.json');
        loader.add('Settings', './assets/accessibility/Settings{size}.json');
        loader.add('playButton', './assets/shared/btn_play.png');

        IntroMenu.preload();
        Menus.preload();

        return loader.load();
    }

    start()
    {
        // Background image
        const texture = LoadContext.current.getTexture('accessibilityBG');
        this.background = new Sprite(texture);
        this.addChild(this.background);
        // set background to interactive to block any clicks through it
        this.background.interactive = true;


        // Keyboard input
        KeyboardJS.setContext('settings');
        const keyListeners = {
            space: (e:KeyboardJS.KeyEvent) => {
                e.preventRepeat();

                this.requestAudioDescription();
            }
        }

        this.disposables = new DisposableGroup();
        // keys bound in a context are attached to just that context
        for (const key in keyListeners)
        {
            this.disposables.add(Disposable.fromKey(key, keyListeners[key]));
        }

        // Tap Input
        this.gestureCatcher = new Container();
        this.addChild(this.gestureCatcher);
        this.gestureCatcher.interactive = true;
        this.gestureCatcher.hitArea = new Rectangle(0, 0, 2160, 1080);
        this.gestureCatcher.on( 'pointertap', (evt: any) =>{
            if( evt.data.global.y > 540) {
                // Bottom half of screen.
                this.requestAudioDescription();
            }
        });

        // Buttons
        this.buttons = [];
        // Play button
        const playButton = new Button(
            LoadContext.current.getTexture('playButton'),
            {
                content: 'PLAY',
                style: {
                    fontName: 'Asap',
                    fontSize: 58,
                    tint: 0x000000,
                },
                offset: {x: 35, y: -10}
            }
        );
        this.disposables.add(new InteractiveWrapper(playButton, {
            onHover: () => {
                this.game.vo.playHighlight('CYB_PanelHome_Play');
            },
            onTap: () => {
                FocusManager.instance.popContext();
                this.game.vo.stop();
                this.groupMenus.visible = false;
                this.introMenu.visible = false;
                this.emit( "close" );
                this.game.settings.settingsOpen.value = true;
            }
        }));
        playButton.position.set( 1050, 975 );
        this.addChild( playButton );
        this.addButton( playButton );

        const resetButton = this.resetButton = new Button(
            LoadContext.current.getSpritesheet('Settings').textures['btn_reset']
        );
        this.disposables.add(new InteractiveWrapper(resetButton, {
            onHover: () => {
                this.game.vo.playHighlight('CYB_PanelHome_Reset');
            },
            onTap: () => {
                // bail when group menus are not visible.
                if (this.viewingIntro) return;
                this.game.vo.stop();
                this.groupMenus.resetActiveGroup();
            }
        }));
        resetButton.position.set( 1595, 1014 );
        resetButton.visible = false;
        resetButton.createHitCircle(30);
        this.addChild( resetButton );
        this.addButton( resetButton );

        const infoButton = new Button(
            LoadContext.current.getSpritesheet('Settings').textures['btn_info']
        );
        this.disposables.add(new InteractiveWrapper(infoButton, {
            onHover: () => {
                this.game.vo.playHighlight('CYB_PanelHome_Info');
            },
            onTap: () => {
                this.game.vo.stop();
                if( this.children.indexOf( this.introMenu ) > -1 )
                {
                    this.introMenu.playInfoAudio();
                }
                else
                {
                    this.groupMenus.playInfoAudio();
                }
            }
        }));
        infoButton.position.set( 1705, 1014 );
        infoButton.createHitCircle(30);
        this.addChild( infoButton );
        this.addButton( infoButton );

        // Menus
        this.introMenu = new IntroMenu( this );
        this.introMenu.on('groupSelect', ( group:Menus.Type )=>{
            this.game.vo.stop();
            // Switch from intro Menu to the correct group.
            this.introMenu.visible = false;
            this.groupMenus.visible = true;
            // Show chosen group;
            this.groupMenus.displayGroup( group );
            resetButton.visible = true;
            this.viewingIntro = false;
        })
        this.addChild( this.introMenu );
        this.viewingIntro = true;

        this.groupMenus = new Menus( this );
        this.addChild(this.groupMenus);
        this.groupMenus.visible = false;
    }

    requestAudioDescription()
    {
        if( this.game.settings.playAudioDescriptions.value ) {
            if( !this.game.vo.playing) {
                if( this.viewingIntro ) {
                    this.introMenu.playAudioDescription();
                } else {
                    this.groupMenus.playAudioDescription();
                }
            }
        }
    }

    public open()
    {
        this.game.logEvent("open_settings");
        // ensure the intro is opened
        this.groupMenus.visible = false;
        this.introMenu.visible = true;
        this.resetButton.visible = true;

        this.introMenu.begin();
        this.viewingIntro = true;

        this.disposables.add(Disposable.fromPropertySubscription(GameTime.gameTick, this.update.bind(this)));
    }

    update(deltaTime:number)
    {
        if (this.game.vo.playing)
        {
            this.inactiveTimer = TIMEOUT;
        }
        else if ((this.inactiveTimer -= deltaTime) <= 0)
        {
            if(this.groupMenus.visible || this.introMenu.visible)
            {
                this.game.vo.playVO('CYB_Panel_Timeout', true);
            }
        }
    }

    cleanup()
    {
        super.destroy({children: true});

        KeyboardJS.setContext('global');
        this.disposables.destroy();
    }

    addButton(button:Button)
    {
        this.buttons.push(button);
    }
}
